import React, { FC } from "react";
import { Box, Grid, Link, Stack, SxProps } from "@mui/material";

import { SubMenu } from "@src/types";
import HeaderSubMenu from "./submenu";
import { Colors } from "@theme/colors";
import MenuHighlight from "./menuHighlight";
import { ArrowDropUpRounded } from "@mui/icons-material";
import { Arrow, navArrowHoverStyles } from "./arrow";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";

interface Props {
  subSections?: Array<SubMenu>;
  containerType?: string;
  arrowColor?: string;
}

const HeaderMenu: FC<Props> = ({
  subSections = [],
  containerType = "",
  arrowColor = "",
}) => {
  return (
    <Box
      sx={{
        marginTop: "15px",
        marginLeft: "-70px",
        position: "relative",
        borderRadius: "8px",
        backgroundColor: Colors.PrimaryContrast,
        boxShadow: "0px 0px 20px rgba(50, 50, 93, 0.25)",
      }}
    >
      <ArrowDropUpRounded
        fontSize="large"
        sx={{
          position: "absolute",
          top: "-33px",
          zIndex: 10,
          left: "68px",
          width: "64px",
          height: "64px",
          color: arrowColor.trim() === "" ? Colors.PrimaryContrast : arrowColor,
        }}
      />
      {containerType === "columns-services" ? (
        <Grid container columns={10}>
          {[1, 2, 3].map((column) => {
            let additionalStyles: SxProps = {};
            let additionalProps: any = {};

            if (column === 1) {
              additionalStyles = {
                background:
                  "linear-gradient(207.09deg, #CB7AF8 0.05%, #1F7EBF 100.05%)",
                padding: "32px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              };
              additionalProps = {
                xs: 4,
              };
            }

            if (column === 2) {
              additionalStyles = {
                padding: "32px 16px 32px 32px",
              };
              additionalProps = {
                xs: 3,
              };
            }

            if (column === 3) {
              additionalStyles = {
                padding: "32px 24px 0px 16px",
              };
              additionalProps = {
                xs: 3,
              };
            }

            return (
              <Grid
                item
                key={`column-${column}`}
                sx={{ ...additionalStyles }}
                {...additionalProps}
              >
                <Stack
                  gap={"32px"}
                  sx={{
                    height: "100%",
                    justifyContent: "flex-start",
                  }}
                >
                  {subSections
                    .filter((subSection) => {
                      if (subSection.layout === `column-${column}`) {
                        return true;
                      }

                      return false;
                    })
                    .map((item) => {
                      return (
                        <HeaderSubMenu
                          key={item.name}
                          title={item.name}
                          layout={item.layout}
                          menuLinks={item.menuLinks}
                          accentColorKey={
                            item.accentColor ? item.accentColor : ""
                          }
                          highlightSection={column === 1 ? true : false}
                          lightIconBg={column === 1 ? true : false}
                          size={column === 1 ? "big" : "default"}
                        />
                      );
                    })}

                  {column === 1 ? (
                    <>
                      <Link
                        href="/signup"
                        sx={{
                          justifySelf: "flex-end",
                          textDecoration: "none",
                          color: Colors.PrimaryContrast,
                          fontSize: "14px",
                          verticalAlign: "middle",
                          opacity: 1,
                          transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                          ".nav-arrow-forward": {
                            color: Colors.PrimaryContrast,
                            stroke: Colors.PrimaryContrast,
                          },
                          "&:hover": {
                            textDecoration: "none",
                            color: Colors.PrimaryContrast,
                            opacity: uiOpacityHover,
                            ...navArrowHoverStyles,
                          },
                        }}
                      >
                        Get started for free today <Arrow />
                      </Link>
                    </>
                  ) : (
                    ""
                  )}
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        ""
      )}

      {containerType === "columns-2" ? (
        <Box
          sx={{
            padding: "32px",
          }}
        >
          <Grid container>
            {[1, 2].map((column) => {
              return (
                <Grid item xs={6} key={`column-${column}`}>
                  <Stack gap={"32px"}>
                    {subSections
                      .filter((subSection) => {
                        if (subSection.layout === `column-${column}`) {
                          return true;
                        }

                        return false;
                      })
                      .map((item) => {
                        return (
                          <HeaderSubMenu
                            key={item.name}
                            title={item.name}
                            layout={item.layout}
                            accentColorKey={
                              item.accentColor ? item.accentColor : ""
                            }
                            menuLinks={item.menuLinks}
                          />
                        );
                      })}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : (
        ""
      )}

      {containerType === "columns-2-highlight" ? (
        <Box
          sx={{
            padding: "32px",
          }}
        >
          <Stack
            direction="column"
            gap="20px"
            sx={{
              width: "100%",
            }}
          >
            <Stack
              gap={"32px"}
              sx={{
                width: "100%",
              }}
            >
              {subSections
                .filter((subSection) => {
                  if (subSection.layout === `highlight`) {
                    return true;
                  }

                  return false;
                })
                .map((item) => {
                  return (
                    <MenuHighlight
                      itemTitle={item.name}
                      subTitle={item.description}
                      url={item?.link}
                      icon={item?.icon}
                      key={`highlight-${item.name}`}
                    />
                  );
                })}
            </Stack>

            <Stack
              direction="row"
              gap="16px"
              sx={{
                width: "100%",
              }}
            >
              {[1, 2].map((column) => {
                return (
                  <Box
                    key={`column-${column}`}
                    sx={{
                      width: "100%",
                    }}
                  >
                    {subSections
                      .filter((subSection) => {
                        if (subSection.layout === `column-${column}`) {
                          return true;
                        }

                        return false;
                      })
                      .map((item) => {
                        return (
                          <HeaderSubMenu
                            key={item.name}
                            title={item.name}
                            layout={item.layout}
                            accentColorKey={
                              item.accentColor ? item.accentColor : ""
                            }
                            menuLinks={item.menuLinks}
                          />
                        );
                      })}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </Box>
      ) : (
        ""
      )}

      {!containerType || containerType?.trim() === ""
        ? subSections.map((item) => {
            return (
              <HeaderSubMenu
                key={item.name}
                layout={item.layout}
                accentColorKey={item.accentColor ? item.accentColor : ""}
                title={item.name}
                menuLinks={item.menuLinks}
              />
            );
          })
        : ""}
    </Box>
  );
};

export default HeaderMenu;
